import React, { useEffect, useState } from 'react';
import { Tabs, Button, Input, Form, Space, Popconfirm, Tag, Spin } from 'antd';
import { PlusOutlined, SaveOutlined, DeleteOutlined } from '@ant-design/icons';
import switches from '../../../../switches';
import { STATUS } from '../../../../redux/constants';
import styles from './indicators.module.sass';
import CountrySelect from '../../../../shared-components/CountrySelect';
import MultipleSelect from '../../../../shared-components/MultipleSelect';
import tagStyles from '../../../../shared-components/Tag/tag.module.sass';
import cx from 'classnames';
import { countries_list } from './../../../../shared-components/CountrySelect/index.js';
import { changeTitle } from '../../../../redux/actions/app';
import { useDispatch } from 'react-redux';
import VersionCheck from '../../../../shared-components/VersionCheck';
import PermissionDeniedPage from '../../../../shared-components/PermissionDeniedPage';
import LoadingSilentPush from '../../../../shared-components/LoadingSilentPush';

const { TabPane } = Tabs;

function Indicators(props) {
  const {
    token,
    status,
    loadCustomDomainIndicatorStatus,
    updateCustomDomainIndicatorStatus,
    deleteCustomDomainIndicatorStatus,
    userPreferences,
    customIPRangeIndicators,
    customGeoIPOrganisationUUID,
    customGeoIpCount,
    customGeoIPList,
    customDomainIndicators,
    companyDomainIndicators,
    brandDomainIndicators,
    supplyDomainIndicators,
  } = props;

  const [checkedItems, setCheckedItems] = useState({});
  const [addNewDomain, setAddNewDomain] = useState(false);
  const [addNewBrand, setAddNewBrand] = useState(false);
  const [addNewSupplier, setAddNewSupplier] = useState(false);

  function handleChange(e, name, index = null, array = null) {
    let value = null;

    let currentIndicators = array && [...checkedItems[array]];

    if (e instanceof Object && !(e instanceof Array)) {
      //coming from input fields
      value = e.target.value;

      //default value for IP field
      if (value === '') {
        value = '0.0.0.0';
      }
    } else {
      value = e;
    }

    if (index !== null) {
      if (!currentIndicators[index]) {
        currentIndicators = [
          ...checkedItems[array],
          {
            ip_range_end: '',
            ip_range_start: '',
          },
        ];
      }
      currentIndicators[index][name] = value;
      setCheckedItems({
        ...checkedItems,
        [array]: currentIndicators,
      });
    }

    if (name === 'customGeoIPList' && typeof e !== 'string') {
      const geoIPList = e;
      props.setCustomGeoIP({ geoIPList, customGeoIPOrganisationUUID });
      setCheckedItems({
        ...checkedItems,
        [name]: geoIPList,
      });
    } else if (name === 'customGeoIPList' && typeof e === 'string') {
      const geoIPList = checkedItems[name]?.filter((el) => el !== value);
      props.setCustomGeoIP({ geoIPList, customGeoIPOrganisationUUID });
      setCheckedItems({
        ...checkedItems,
        [name]: geoIPList,
      });
    }
  }

  function indicatorsSave(key) {
    let ip_range_start =
      checkedItems['customIPRangeIndicators'][key] &&
      checkedItems['customIPRangeIndicators'][key].ip_range_start;
    let ip_range_end =
      checkedItems['customIPRangeIndicators'][key] &&
      checkedItems['customIPRangeIndicators'][key].ip_range_end;
    let uuid =
      checkedItems['customIPRangeIndicators'][key] &&
      checkedItems['customIPRangeIndicators'][key].uuid;

    if (
      checkedItems['customIPRangeIndicators'][key] &&
      ip_range_start &&
      ip_range_end
    ) {
      if (uuid) {
        props.updateCustomIPRangeIndicators({
          ip_range_start,
          ip_range_end,
          uuid,
        });
      } else if (!uuid) {
        props.setCustomIPRangeIndicators({ ip_range_start, ip_range_end });
      }
    }
  }

  function indicatorsDelete(key) {
    let uuid =
      checkedItems['customIPRangeIndicators'][key] &&
      checkedItems['customIPRangeIndicators'][key].uuid;

    if (checkedItems['customIPRangeIndicators'][key] && uuid) {
      props.deleteCustomIPRangeIndicators({ uuid });
    }
  }

  function handleCompanyDomainIndicatorDelete(tag) {
    const deleteDomainUuid = customDomainIndicators
      ?.filter((el) => el?.domain_type === 'company')
      ?.filter((el) => el?.domain === tag)[0]?.uuid;

    props.deleteCustomDomainIndicators({ deleteDomainUuid });
  }

  function handleBrandDomainIndicatorDelete(tag) {
    const deleteDomainUuid = customDomainIndicators
      ?.filter((el) => el?.domain_type === 'brand')
      ?.filter((el) => el?.domain === tag)[0]?.uuid;

    props.deleteCustomDomainIndicators({ deleteDomainUuid });
  }

  function handleSupplierDomainIndicatorDelete(tag) {
    const deleteDomainUuid = customDomainIndicators
      ?.filter((el) => el?.domain_type === 'supply')
      ?.filter((el) => el?.domain === tag)[0]?.uuid;

    props.deleteCustomDomainIndicators({ deleteDomainUuid });
  }

  useEffect(() => {
    document.title = 'Assets - Silent Push';
  }, []);

  useEffect(() => {
    props.loadMySubscriptionPlan();
    props.loadUserPreferences();
    props.loadOrganization();
    props.loadCustomIPRangeIndicators();
    props.loadCustomGeoIPOrganisation();
    props.loadCustomDomainIndicators();
  }, [token, props.history]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    customGeoIPOrganisationUUID &&
      props.loadCustomGeoIP(customGeoIPOrganisationUUID);
  }, [customGeoIPOrganisationUUID]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCheckedItems({
      ...userPreferences,
      customGeoIPList,
      customGeoIpCount,
      customIPRangeIndicators,
    });
  }, [status, customGeoIPList, customIPRangeIndicators]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoadingFinished = status === STATUS.LOADED;
  const dispatch = useDispatch();
  dispatch(changeTitle('Organization Assets', null));
  return props.mySubscriptionPlanStatus === STATUS.LOADED ? (
    props.mySubscriptionPlan?.has_access_to_threat_ranking ? (
      <>
        {!isLoadingFinished ||
          !customIPRangeIndicators ||
          (!customGeoIPList && <Spin style={{ margin: 400 }} />)}
        {isLoadingFinished && customIPRangeIndicators && customGeoIPList && (
          <div className={styles.tab_container}>
            <Tabs defaultActiveKey="1" type="card">
              <TabPane
                className={styles.tab_pane}
                tab="Custom Indicators"
                key="1"
              >
                <div className={styles.domain_indicators}>
                  <h3 className={styles.domain_label}>Domain</h3>
                  <div className={styles.form_control}>
                    <div className={styles.form_control_header}>
                      <label className={styles.label}>
                        Similarity to Domain
                      </label>
                    </div>
                    <div className={styles.form_control_content}>
                      <div className={styles.form_control_select}>
                        {!addNewDomain &&
                          companyDomainIndicators &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            companyDomainIndicators
                              ?.split(',')
                              ?.map((tag, id) => (
                                <Tag
                                  key={`${tag}+${id}`}
                                  closable
                                  className={cx(tagStyles.tag_indicators)}
                                  onClose={() =>
                                    handleCompanyDomainIndicatorDelete(tag)
                                  }
                                >
                                  {tag}
                                </Tag>
                              ))
                          ) : (
                            <Spin />
                          ))}
                        {addNewDomain &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            <MultipleSelect
                              defaultValue={companyDomainIndicators}
                              customDomainIndicators={
                                customDomainIndicators &&
                                customDomainIndicators?.filter(
                                  (el) => el?.domain_type === 'company',
                                )
                              }
                              loadCustomDomainIndicators={
                                props.loadCustomDomainIndicators
                              }
                              setCustomDomainIndicators={
                                props.setCustomDomainIndicators
                              }
                              deleteCustomDomainIndicators={
                                props.deleteCustomDomainIndicators
                              }
                              domain_type="company"
                              className={styles.input}
                            />
                          ) : (
                            <Spin />
                          ))}
                      </div>

                      <div className={styles.form_control_domain_button}>
                        {addNewDomain ? (
                          <Button
                            aria-label={'similarityFinish'}
                            size="small"
                            onClick={() => setAddNewDomain(!addNewDomain)}
                          >
                            Finish
                          </Button>
                        ) : (
                          <Button
                            aria-label={'similarityToDomain'}
                            size="small"
                            onClick={() => setAddNewDomain(!addNewDomain)}
                            icon={<PlusOutlined />}
                          >
                            Add Domain
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.form_control}>
                    <div className={styles.form_control_header}>
                      <label className={styles.label}>
                        Supply Chain Domain
                      </label>
                    </div>
                    <div className={styles.form_control_content}>
                      <div className={styles.form_control_select}>
                        {!addNewSupplier &&
                          supplyDomainIndicators &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            supplyDomainIndicators
                              ?.split(',')
                              ?.map((tag, id) => (
                                <Tag
                                  key={`${tag}+${id}`}
                                  closable
                                  className={cx(tagStyles.tag_indicators)}
                                  onClose={() =>
                                    handleSupplierDomainIndicatorDelete(tag)
                                  }
                                >
                                  {tag}
                                </Tag>
                              ))
                          ) : (
                            <Spin />
                          ))}
                        {addNewSupplier &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            <MultipleSelect
                              defaultValue={supplyDomainIndicators}
                              customDomainIndicators={
                                customDomainIndicators &&
                                customDomainIndicators?.filter(
                                  (el) => el?.domain_type === 'supply',
                                )
                              }
                              loadCustomDomainIndicators={
                                props.loadCustomDomainIndicators
                              }
                              setCustomDomainIndicators={
                                props.setCustomDomainIndicators
                              }
                              deleteCustomDomainIndicators={
                                props.deleteCustomDomainIndicators
                              }
                              domain_type="supply"
                              className={styles.input}
                            />
                          ) : (
                            <Spin />
                          ))}
                      </div>

                      <div className={styles.form_control_domain_button}>
                        {addNewSupplier ? (
                          <Button
                            aria-label="supplyFinish"
                            size="small"
                            onClick={() => setAddNewSupplier(!addNewSupplier)}
                          >
                            Finish
                          </Button>
                        ) : (
                          <Button
                            aria-label="supplyAdd"
                            size="small"
                            onClick={() => setAddNewSupplier(!addNewSupplier)}
                            icon={<PlusOutlined />}
                          >
                            Add Domain
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className={styles.form_control}>
                    <div className={styles.form_control_header}>
                      <label className={styles.label}>Top Brands Domain</label>
                    </div>
                    <div className={styles.form_control_content}>
                      <div className={styles.form_control_select}>
                        {!addNewBrand &&
                          brandDomainIndicators &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            brandDomainIndicators
                              ?.split(',')
                              ?.map((tag, id) => (
                                <Tag
                                  key={`${tag}+${id}`}
                                  closable
                                  className={cx(tagStyles.tag_indicators)}
                                  onClose={() =>
                                    handleBrandDomainIndicatorDelete(tag)
                                  }
                                >
                                  {tag}
                                </Tag>
                              ))
                          ) : (
                            <Spin />
                          ))}
                        {addNewBrand &&
                          (loadCustomDomainIndicatorStatus !== STATUS.LOADING &&
                          updateCustomDomainIndicatorStatus !==
                            STATUS.LOADING &&
                          deleteCustomDomainIndicatorStatus !==
                            STATUS.LOADING ? (
                            <MultipleSelect
                              defaultValue={brandDomainIndicators}
                              customDomainIndicators={
                                customDomainIndicators &&
                                customDomainIndicators?.filter(
                                  (el) => el?.domain_type === 'brand',
                                )
                              }
                              loadCustomDomainIndicators={
                                props.loadCustomDomainIndicators
                              }
                              setCustomDomainIndicators={
                                props.setCustomDomainIndicators
                              }
                              deleteCustomDomainIndicators={
                                props.deleteCustomDomainIndicators
                              }
                              domain_type="brand"
                              className={styles.input}
                            />
                          ) : (
                            <Spin />
                          ))}
                      </div>

                      <div className={styles.form_control_domain_button}>
                        {addNewBrand ? (
                          <Button
                            aria-label="brandsFinish"
                            size="small"
                            onClick={() => setAddNewBrand(!addNewBrand)}
                          >
                            Finish
                          </Button>
                        ) : (
                          <Button
                            aria-label="brandsAdd"
                            size="small"
                            onClick={() => setAddNewBrand(!addNewBrand)}
                            icon={<PlusOutlined />}
                          >
                            Add Domain
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.ip_indicators}>
                  <h3 className={styles.ip_label}>IP</h3>
                  {isLoadingFinished &&
                    customIPRangeIndicators &&
                    switches.map((item) => {
                      if (item?.tab === 'custom') {
                        return (
                          <div className="tab_item" key={item.key}>
                            {
                              {
                                geo_ip: (
                                  <>
                                    <div className={styles.form_control}>
                                      <div
                                        className={styles.form_control_header}
                                      >
                                        <label className={styles.label}>
                                          GEO
                                        </label>
                                      </div>
                                      <div
                                        className={styles.form_control_content}
                                      >
                                        <div
                                          className={
                                            styles.form_control_geo_tags
                                          }
                                        >
                                          {customGeoIPList?.map((tag, id) => (
                                            <Tag
                                              key={`${tag}+${id}`}
                                              closable
                                              className={cx(
                                                tagStyles.tag_indicators,
                                              )}
                                              onClose={() =>
                                                handleChange(
                                                  tag,
                                                  'customGeoIPList',
                                                )
                                              }
                                            >
                                              {
                                                countries_list?.filter(
                                                  (el) => el?.key === tag,
                                                )[0]?.props?.children
                                              }
                                            </Tag>
                                          ))}
                                        </div>

                                        <div
                                          className={
                                            styles.form_control_geo_input
                                          }
                                        >
                                          <label>Select Country</label>
                                          <CountrySelect
                                            onChange={(e) =>
                                              handleChange(e, 'customGeoIPList')
                                            }
                                            value={
                                              checkedItems['customGeoIPList']
                                            }
                                            style={{
                                              width: '392px',
                                              marginRight: '5px',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                            }}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                ),
                                ip_range_weight: (
                                  <>
                                    <div className={styles.form_control}>
                                      <div
                                        className={styles.form_control_header}
                                      >
                                        <label className={styles.label}>
                                          IP Range
                                        </label>
                                      </div>
                                      <div
                                        className={styles.form_control_content}
                                      >
                                        <div
                                          className={
                                            styles.form_control_range_indicators
                                          }
                                        >
                                          <Form
                                            name="dynamic_form_nest_item"
                                            autoComplete="off"
                                            initialValues={{
                                              customIPRangeIndicators,
                                            }}
                                          >
                                            <Form.List name="customIPRangeIndicators">
                                              {(fields, { add, remove }) => (
                                                <>
                                                  {fields?.map(
                                                    (field, index) => (
                                                      <div
                                                        key={field?.key}
                                                        className={
                                                          styles.ip_range_form_div
                                                        }
                                                      >
                                                        <Space
                                                          style={{
                                                            display: 'flex',
                                                          }}
                                                          align="baseline"
                                                        >
                                                          <Form.Item
                                                            {...field}
                                                            name={[
                                                              field?.name,
                                                              'ip_range_start',
                                                            ]}
                                                            fieldKey={[
                                                              field?.fieldKey,
                                                              'ip_range_start',
                                                            ]}
                                                            rules={[
                                                              {
                                                                pattern: new RegExp(
                                                                  /^(?=\d+\.\d+\.\d+\.\d+$)(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.?){4}$/,
                                                                ),
                                                                message:
                                                                  'Please enter a valid IP',
                                                              },
                                                            ]}
                                                          >
                                                            <Input
                                                              style={{
                                                                width: '200px',
                                                                marginRight:
                                                                  '5px',
                                                                borderRadius:
                                                                  '4px',
                                                              }}
                                                              placeholder="ip_range_start"
                                                              defaultValue={
                                                                field?.ip_range_start
                                                              }
                                                              onChange={(e) =>
                                                                handleChange(
                                                                  e,
                                                                  'ip_range_start',
                                                                  field?.key,
                                                                  'customIPRangeIndicators',
                                                                )
                                                              }
                                                            />
                                                          </Form.Item>
                                                          <Form.Item
                                                            {...field}
                                                            name={[
                                                              field?.name,
                                                              'ip_range_end',
                                                            ]}
                                                            fieldKey={[
                                                              field?.fieldKey,
                                                              'ip_range_end',
                                                            ]}
                                                            rules={[
                                                              {
                                                                pattern: new RegExp(
                                                                  /^(?=\d+\.\d+\.\d+\.\d+$)(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]|[0-9])\.?){4}$/,
                                                                ),
                                                                message:
                                                                  'Please enter a valid IP',
                                                              },
                                                            ]}
                                                          >
                                                            <Input
                                                              style={{
                                                                width: '200px',
                                                                marginRight:
                                                                  '5px',
                                                                borderRadius:
                                                                  '4px',
                                                              }}
                                                              placeholder={
                                                                'ip_range_end'
                                                              }
                                                              defaultValue={
                                                                field?.ip_range_end
                                                              }
                                                              onChange={(e) =>
                                                                handleChange(
                                                                  e,
                                                                  'ip_range_end',
                                                                  field?.key,
                                                                  'customIPRangeIndicators',
                                                                )
                                                              }
                                                            />
                                                          </Form.Item>
                                                          <SaveOutlined
                                                            style={{
                                                              color: '#ccd4d9',
                                                            }}
                                                            onClick={() =>
                                                              indicatorsSave(
                                                                field.key,
                                                              )
                                                            }
                                                          />
                                                          <Popconfirm
                                                            title={
                                                              <div
                                                                style={{
                                                                  color:
                                                                    '#ccd4d9',
                                                                }}
                                                              >
                                                                Are you sure?
                                                              </div>
                                                            }
                                                            style={{
                                                              color: '#ccd4d9',
                                                            }}
                                                            onConfirm={() => {
                                                              indicatorsDelete(
                                                                field?.key,
                                                              );
                                                              remove(
                                                                field?.name,
                                                              );
                                                            }}
                                                          >
                                                            <DeleteOutlined
                                                              style={{
                                                                color:
                                                                  '#ccd4d9',
                                                              }}
                                                            />
                                                          </Popconfirm>
                                                        </Space>
                                                      </div>
                                                    ),
                                                  )}
                                                  <Form.Item>
                                                    <div
                                                      style={{
                                                        fontSize: 13,
                                                        margin: '0 auto',
                                                        display: 'flex',
                                                        flexDirection:
                                                          'row-reverse',
                                                      }}
                                                    >
                                                      <PlusOutlined
                                                        aria-label="plusOutlined"
                                                        style={{
                                                          color:
                                                            'rgba(255, 255, 255, 0.85)',
                                                        }}
                                                        onClick={() => add()}
                                                      />
                                                    </div>
                                                  </Form.Item>
                                                </>
                                              )}
                                            </Form.List>
                                          </Form>
                                        </div>
                                      </div>
                                    </div>
                                  </>
                                ),
                              }[item.key]
                            }
                          </div>
                        );
                      } else {
                        return null;
                      }
                    })}
                </div>
              </TabPane>
            </Tabs>
          </div>
        )}
        <VersionCheck
          parentComponentStatus={props.loadCustomDomainIndicatorStatus}
        />
      </>
    ) : (
      <PermissionDeniedPage />
    )
  ) : (
    <LoadingSilentPush size={80} positionCenter />
  );
}

export default Indicators;
